import { getStaticCookie } from 'src/components/MetaTags/BootLaunchDarkly';

export type LDFlagValue = string | number | boolean;

export type LDContextValue = string | number | boolean;

export type LDFlags = Record<string, LDFlagValue>;

export type LDContext = Record<string, LDContextValue>;

export type LDAnonymousId = string;

// These cookies are set by the backend `webdev-lambdas` service and are used to pass
// experimentation information along to the frontend for us to parse and decide
// to conditionally render content as part of A/B experiments.
// @see [redacted]#################################################################################

/** Persistent LaunchDarkly identifier */
const LD_COOKIE_ANON_ID = 'ld_anonymous_id';

/** Ephemeral object of flags and their values for current user on current page */
const LD_COOKIE_FLAGS = 'ld_flags';

/** Persistent object of LaunchDarkly context */
const LD_COOKIE_CONTEXT = 'ld_context';

/** Retrieve current visitor's LD identifier */
export function getLDAnonymousId(): LDAnonymousId | null {
  return getStaticCookie(LD_COOKIE_ANON_ID);
}

/** Safely parse a JSON object string into an object with a desired key/value type. */
function safelyParseJSON<K extends string | number | symbol, V>(
  maybeJSON: string,
  keyTypeOfQualifier: (k: unknown) => k is K,
  valueTypeOfQualifier: (v: unknown) => v is V,
): Partial<Record<K, V>> {
  if (!maybeJSON) {
    return {};
  }

  let parsedJSON: unknown;

  try {
    parsedJSON = JSON.parse(maybeJSON);
  } catch (error) {
    return {};
  }

  if (!parsedJSON || typeof parsedJSON !== 'object') {
    return {};
  }

  const sanitizedJSON: Partial<Record<K, V>> = {};

  for (const [key, value] of Object.entries(parsedJSON)) {
    if (keyTypeOfQualifier(key) && valueTypeOfQualifier(value)) {
      sanitizedJSON[key] = value;
    }
  }

  return sanitizedJSON;
}

export function getLDFlags(): LDFlags {
  const cookieFlags = getStaticCookie(LD_COOKIE_FLAGS);

  return safelyParseJSON<string, LDFlagValue>(
    cookieFlags,
    (key) => {
      return typeof key === 'string';
    },
    (value) => {
      return (
        typeof value === 'boolean' ||
        typeof value === 'string' ||
        typeof value === 'number'
      );
    },
  );
}

export function getLDContext(): LDContext {
  const cookieContext = getStaticCookie(LD_COOKIE_CONTEXT);

  return safelyParseJSON<string, LDContextValue>(
    cookieContext,
    (key) => {
      return typeof key === 'string';
    },
    (value) => {
      return (
        typeof value === 'boolean' ||
        typeof value === 'string' ||
        typeof value === 'number'
      );
    },
  );
}

let ldPayloadForSegment: {
  ld: {
    anonId: LDAnonymousId;
    flags: LDFlags;
    context: LDContext;
  };
};

export function getLDPayloadForSegment() {
  return (ldPayloadForSegment ??= {
    ld: {
      anonId: getLDAnonymousId(),
      flags: getLDFlags(),
      context: getLDContext(),
    },
  });
}
